import React from 'react';

import { getJSON, postJSON } from '../../api/client.js';
import {
  CrudPage, FeaturesPage, HelpIndexPage, HookTester, OrgSettingsForm, SoloSettings,
  THEME, UniActionButton, UniKpiCard, UniPageShell, UniSection, UniStatusPill,
  UniTable, UniTile, WABA_API, WABA_V1, fmtCount, useUniList,
} from './WabaShared.jsx';

const HOME = '/waba-chat';
const P = { theme: THEME, api: WABA_API, home: HOME };

export function WabaDashboard() {
  const summary = useUniList(`${WABA_API}/analytics/summary/`);
  const s = summary.raw || {};
  const v = (n) => (summary.loading ? '…' : fmtCount(n));
  return (
    <UniPageShell
      theme={THEME}
      icon="💬"
      title="WhatsApp Business"
      subtitle="Inbox, broadcasts, templates, Bot Studio and RAG AI — this organisation's schema only."
      helpPage="dashboard"
      helpApi={WABA_API}
      actions={(
        <>
          <UniActionButton hero primary to="/waba-chat/inbox">Open inbox</UniActionButton>
          <UniActionButton hero to="/waba-chat/broadcasts">Broadcasts</UniActionButton>
          <UniActionButton hero to="/waba-chat/settings">Settings</UniActionButton>
        </>
      )}
    >
      <UniSection title="At a glance" />
      <div className="uni-kpis">
        <UniKpiCard icon="💬" label="Open chats" value={v(s.conversations?.open)} to="/waba-chat/inbox" />
        <UniKpiCard icon="📬" label="Unread" value={v(s.conversations?.unread)} to="/waba-chat/inbox" />
        <UniKpiCard icon="👤" label="Contacts" value={v(s.contacts)} to="/waba-chat/contacts" />
        <UniKpiCard icon="📣" label="Broadcasts" value={v(s.broadcasts)} to="/waba-chat/broadcasts" />
        <UniKpiCard icon="✅" label="Approved templates" value={v(s.templates_approved)} to="/waba-chat/templates" />
        <UniKpiCard icon="⚠️" label="Failed messages" value={v(s.messages?.failed)} to="/waba-chat/failed" />
      </div>
      <UniSection title="Open" />
      <div className="uni-tiles">
        <UniTile to="/waba-chat/inbox" icon="💬" title="Inbox" sub="Live chats and the 24-hour window" />
        <UniTile to="/waba-chat/contacts" icon="👤" title="Contacts" sub="Opt-in, tags, CSV import" />
        <UniTile to="/waba-chat/lists" icon="📋" title="Lists" sub="Audiences for campaigns" />
        <UniTile to="/waba-chat/broadcasts" icon="📣" title="Broadcasts" sub="Template campaigns" />
        <UniTile to="/waba-chat/templates" icon="📄" title="Templates" sub="Sync from Meta / AWS" />
        <UniTile to="/waba-chat/media" icon="🖼" title="Media" sub="Images, audio, documents" />
        <UniTile to="/waba-chat/analytics" icon="📊" title="Analytics" sub="Volume and delivery" />
        <UniTile to="/waba-chat/webhooks" icon="🔗" title="Webhooks" sub="Outgoing signed callbacks" />
        <UniTile to="/waba-chat/flows" icon="🔀" title="Bot Studio" sub="Keyword and button flows" />
        <UniTile to="/waba-chat/settings" icon="⚙️" title="Settings" sub="Org AWS, webhook token, dialogs" />
        <UniTile to="/waba-chat/numbers" icon="📱" title="Numbers" sub="Meta tokens and AWS phone ARNs" />
        <UniTile to="/waba-chat/failed" icon="⚠️" title="Failed" sub="Retry rejected sends" />
        <UniTile to="/waba-chat/hooks" icon="🔌" title="Hooks" sub="Forms / CRM / Shop / KYC" />
        <UniTile to="/waba-chat/api-keys" icon="🔑" title="API keys" sub="DoubleTick-style send API" />
        <UniTile to="/waba-chat/health" icon="💚" title="Health" sub="Webhook URLs and number status" />
        <UniTile to="/waba-chat/notes" icon="🗒" title="Notes" sub="Internal comments on threads" />
        <UniTile to="/waba-chat/incoming-logs" icon="📥" title="Incoming logs" sub="Meta / SNS / hook payloads" />
        <UniTile to="/waba-chat/help" icon="❓" title="Help" sub="Every stage, same copy on mobile" />
        <UniTile to="/waba-chat/features" icon="✨" title="Features" sub="Full capability list" />
        <UniTile to="/waba-chat/automation" icon="⏱" title="Automation" sub="Cron fan-out health" />
      </div>
    </UniPageShell>
  );
}

export function WabaContacts() {
  return (
    <CrudPage
      {...P} helpPage="contacts" icon="👤" title="Contacts"
      endpoint={`${WABA_V1}/contacts/`}
      searchPlaceholder="Search phone or name…"
      createLabel="+ Contact"
      fields={[
        { key: 'phone_number', label: 'Phone', required: true, hint: 'E.164, e.g. +919876543210' },
        { key: 'name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'user_type', label: 'User type', type: 'select', options: [
          { value: 'unknown', label: 'Unknown' },
          { value: 'customer', label: 'Customer' },
          { value: 'distributor', label: 'Distributor' },
        ] },
        { key: 'opted_in', label: 'Opted in', type: 'check' },
        { key: 'is_blocked', label: 'Blocked', type: 'check' },
      ]}
    />
  );
}

export function WabaLists() {
  return (
    <CrudPage
      {...P} helpPage="lists" icon="📋" title="Contact lists"
      endpoint={`${WABA_V1}/contact-lists/`}
      searchPlaceholder="Search lists…"
      createLabel="+ List"
      fields={[
        { key: 'name', label: 'Name', required: true },
        { key: 'description', label: 'Description', type: 'textarea' },
      ]}
    />
  );
}

export function WabaTags() {
  return (
    <CrudPage
      {...P} helpPage="contacts" icon="🏷" title="Tags"
      endpoint={`${WABA_V1}/tags/`}
      createLabel="+ Tag"
      fields={[
        { key: 'name', label: 'Name', required: true },
        { key: 'color', label: 'Colour', hint: '#25d366' },
      ]}
    />
  );
}

export function WabaLabels() {
  return (
    <CrudPage
      {...P} helpPage="inbox" icon="🏷" title="Conversation labels"
      endpoint={`${WABA_V1}/labels/`}
      createLabel="+ Label"
      fields={[
        { key: 'name', label: 'Name', required: true },
        { key: 'color', label: 'Colour' },
      ]}
    />
  );
}

export function WabaBroadcasts() {
  return (
    <CrudPage
      {...P} helpPage="broadcasts" icon="📣" title="Broadcasts"
      endpoint={`${WABA_V1}/broadcasts/`}
      searchPlaceholder="Search campaigns…"
      createLabel="+ Campaign"
      fields={[
        { key: 'name', label: 'Name', required: true },
        { key: 'contact_list', label: 'Contact list id', type: 'number', required: true },
        { key: 'template', label: 'Template id', type: 'number', required: true },
        { key: 'waba_config', label: 'WhatsApp number id', type: 'number' },
        { key: 'scheduled_at', label: 'Schedule (ISO datetime)' },
        { key: 'header_media_url', label: 'Header media URL' },
      ]}
    />
  );
}

export function WabaTemplates() {
  const sync = async () => {
    await postJSON(`${WABA_API}/templates/sync/`, {});
    window.location.reload();
  };
  return (
    <CrudPage
      {...P} helpPage="templates" icon="📄" title="Message templates"
      endpoint={`${WABA_V1}/templates/`}
      searchPlaceholder="Search templates…"
      createLabel="+ Template"
      extraActions={<UniActionButton hero onClick={sync}>Sync from provider</UniActionButton>}
      fields={[
        { key: 'name', label: 'Name', required: true },
        { key: 'language', label: 'Language', hint: 'e.g. en_US' },
        { key: 'category', label: 'Category' },
        { key: 'status', label: 'Status' },
        { key: 'body', label: 'Body', type: 'textarea' },
        { key: 'waba_config', label: 'Number id', type: 'number' },
      ]}
    />
  );
}

export function WabaMedia() {
  const { raw, loading, error } = useUniList(`${WABA_API}/media/`);
  const rows = raw?.results || [];
  return (
    <UniPageShell
      {...P} icon="🖼" title="Media library" helpPage="media"
      actions={<UniActionButton hero to={HOME}>← Home</UniActionButton>}
    >
      {loading && <div className="uni-state">Loading…</div>}
      {error && <div className="ty-error">{String(error.message || error)}</div>}
      <div className="uni-tiles">
        {rows.map((m) => (
          <a key={m.id} className="uni-tile" href={m.media_url} target="_blank" rel="noreferrer">
            <div className="uni-tile__icon">📎</div>
            <div>
              <div className="uni-tile__title">{m.media_filename || m.media_url}</div>
              <div className="uni-tile__sub">{m.media_mime_type} · conv {m.conversation_id}</div>
            </div>
          </a>
        ))}
      </div>
      {rows.length === 0 && !loading && <div className="uni-state">No media yet</div>}
    </UniPageShell>
  );
}

export function WabaAnalytics() {
  const summary = useUniList(`${WABA_API}/analytics/summary/`);
  const ads = useUniList(`${WABA_V1}/ad-performance/`);
  return (
    <UniPageShell
      {...P} icon="📊" title="Analytics" helpPage="analytics"
      actions={<UniActionButton hero to={HOME}>← Home</UniActionButton>}
    >
      <pre className="uni-note" style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
        {summary.loading ? 'Loading…' : JSON.stringify(summary.raw, null, 2)}
      </pre>
      <UniSection title="Click-to-WhatsApp ads" />
      <pre className="uni-note" style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
        {ads.loading ? 'Loading…' : JSON.stringify(ads.raw, null, 2)}
      </pre>
    </UniPageShell>
  );
}

export function WabaWebhooks() {
  return (
    <CrudPage
      {...P} helpPage="webhooks" icon="🔗" title="Outgoing webhooks"
      endpoint={`${WABA_V1}/webhooks/`}
      createLabel="+ Webhook"
      extraActions={<UniActionButton hero to="/waba-chat/incoming-logs">Incoming logs</UniActionButton>}
      fields={[
        { key: 'name', label: 'Name', required: true },
        { key: 'url', label: 'HTTPS URL', required: true },
        { key: 'secret', label: 'HMAC secret' },
        { key: 'is_active', label: 'Active', type: 'check' },
        { key: 'events', label: 'Events JSON', type: 'json' },
      ]}
    />
  );
}

export function WabaIncomingLogs() {
  return (
    <CrudPage
      {...P} helpPage="webhooks" icon="📥" title="Incoming webhook logs"
      endpoint={`${WABA_API}/incoming-logs/`}
      readOnly
      fields={[
        { key: 'event_type', label: 'Event' },
        { key: 'source', label: 'Source' },
        { key: 'processed', label: 'Processed', type: 'check' },
        { key: 'error', label: 'Error', type: 'textarea' },
      ]}
    />
  );
}

export function WabaSettings() {
  return (
    <OrgSettingsForm
      {...P}
      helpPage="settings"
      icon="⚙️"
      title="WhatsApp settings"
      subtitle="Organisation credentials for this company. Not read from settings.py."
      endpoint={`${WABA_API}/org-settings/`}
      extraActions={(
        <>
          <UniActionButton hero primary to="/waba-chat/numbers">Numbers</UniActionButton>
          <UniActionButton hero to="/waba-chat/s3">S3</UniActionButton>
          <UniActionButton hero to="/waba-chat/retry">Retries</UniActionButton>
          <UniActionButton hero to="/waba-chat/throttle">Throttle</UniActionButton>
        </>
      )}
    />
  );
}

export function WabaNumbers() {
  return (
    <CrudPage
      {...P} helpPage="numbers" icon="📱" title="WhatsApp numbers"
      endpoint={`${WABA_API}/configs/`}
      createLabel="+ Number"
      extraActions={(
        <>
          <UniActionButton hero to="/waba-chat/settings">Org settings</UniActionButton>
          <UniActionButton hero to="/waba-chat/quick-replies">Quick replies</UniActionButton>
          <UniActionButton hero to="/waba-chat/auto-replies">Auto replies</UniActionButton>
          <UniActionButton hero to="/waba-chat/teams">Teams</UniActionButton>
          <UniActionButton hero to="/waba-chat/form-dialogs">Form dialogs</UniActionButton>
        </>
      )}
      fields={[
        { key: 'phone_number', label: 'Phone', required: true },
        { key: 'business_name', label: 'Business name', required: true },
        { key: 'provider', label: 'Provider', type: 'select', options: [
          { value: 'meta_cloud', label: 'Meta Cloud' },
          { value: 'aws_sns', label: 'AWS Social Messaging' },
        ] },
        { key: 'is_active', label: 'Active', type: 'check' },
        { key: 'is_default', label: 'Default sender', type: 'check' },
        { key: 'meta_phone_number_id', label: 'Meta phone number id' },
        { key: 'meta_business_account_id', label: 'Meta WABA id' },
        { key: 'meta_access_token', label: 'Meta access token', type: 'password' },
        { key: 'meta_app_secret', label: 'Meta app secret', type: 'password' },
        { key: 'aws_phone_number_arn', label: 'AWS phone ARN' },
        { key: 'aws_sns_topic_arn', label: 'SNS topic ARN' },
        { key: 'ai_enabled', label: 'RAG AI enabled', type: 'check' },
      ]}
    />
  );
}

export function WabaS3() {
  return (
    <CrudPage
      {...P} helpPage="settings" icon="☁️" title="S3 media"
      endpoint={`${WABA_API}/s3-configs/`}
      createLabel="+ S3 config"
      fields={[
        { key: 'bucket_name', label: 'Bucket', required: true },
        { key: 'region', label: 'Region' },
        { key: 'prefix', label: 'Key prefix' },
        { key: 'is_enabled', label: 'Enabled', type: 'check' },
      ]}
    />
  );
}

export function WabaRetry() {
  return (
    <SoloSettings
      {...P} helpPage="failed" icon="🔁" title="Failed-message retry"
      getUrl={`${WABA_API}/retry-settings/`}
      fields={[
        { key: 'is_enabled', label: 'Enabled', type: 'check' },
        { key: 'max_retries', label: 'Max retries', type: 'number' },
        { key: 'retry_interval_hours', label: 'Interval (hours)', type: 'number' },
      ]}
    />
  );
}

export function WabaThrottle() {
  return (
    <SoloSettings
      {...P} helpPage="settings" icon="🚦" title="Send throttle"
      getUrl={`${WABA_API}/throttle-settings/`}
      fields={[
        { key: 'load_check_enabled', label: 'Load check enabled', type: 'check' },
        { key: 'load_threshold_ratio', label: 'Load threshold ratio', type: 'number' },
        { key: 'load_threshold_offset', label: 'Load threshold offset', type: 'number' },
        { key: 'load_poll_interval_seconds', label: 'Poll interval (seconds)', type: 'number' },
      ]}
    />
  );
}

export function WabaFailed() {
  const { rows, loading, error, reload } = useUniList(`${WABA_API}/failed-messages/`);
  const retry = async (id) => {
    await postJSON(`${WABA_API}/failed-messages/${id}/retry/`, {});
    reload();
  };
  const abandon = async (id) => {
    await postJSON(`${WABA_API}/failed-messages/${id}/abandon/`, {});
    reload();
  };
  return (
    <UniPageShell
      {...P} icon="⚠️" title="Failed messages" helpPage="failed"
      actions={(
        <>
          <UniActionButton hero to="/waba-chat/retry">Retry settings</UniActionButton>
          <UniActionButton hero to={HOME}>← Home</UniActionButton>
        </>
      )}
    >
      <UniTable
        rows={rows} loading={loading} error={error} rowKey="id"
        empty="No failed messages"
        columns={[
          { key: 'id', label: 'ID' },
          { key: 'content', label: 'Content', sortValue: (m) => m.content, render: (m) => (m.content || '').slice(0, 80) },
          { key: 'status', label: 'Status', render: (m) => <UniStatusPill value={m.status} /> },
          { key: 'error_message', label: 'Error', sortValue: (m) => m.error_message || m.failure_reason, render: (m) => m.error_message || m.failure_reason },
          {
            key: 'actions',
            label: 'Actions',
            sortable: false,
            render: (m) => (
              <>
                <button type="button" className="uni-row-btn" onClick={() => retry(m.id)}>Retry</button>
                <button type="button" className="uni-row-btn uni-row-btn--danger" onClick={() => abandon(m.id)}>Abandon</button>
              </>
            ),
          },
        ]}
      />
    </UniPageShell>
  );
}

export function WabaAutomation() {
  const { raw, loading, error } = useUniList(`${WABA_API}/automation/status/`);
  return (
    <UniPageShell
      {...P} icon="⏱" title="Automation" helpPage="automation"
      actions={<UniActionButton hero to={HOME}>← Home</UniActionButton>}
    >
      <UniTable
        rows={raw?.jobs || []} loading={loading} error={error} rowKey="task"
        empty="No scheduled jobs"
        columns={[
          { key: 'schedule', label: 'Schedule' },
          { key: 'task', label: 'Task' },
        ]}
      />
    </UniPageShell>
  );
}

export function WabaApiKeys() {
  return (
    <CrudPage
      {...P} helpPage="api" icon="🔑" title="API keys"
      endpoint={`${WABA_V1}/api-keys/`}
      createLabel="+ Key"
      fields={[
        { key: 'name', label: 'Name', required: true },
        { key: 'can_send_messages', label: 'Can send messages', type: 'check' },
        { key: 'can_send_bulk', label: 'Can send bulk', type: 'check' },
        { key: 'can_manage_contacts', label: 'Can manage contacts', type: 'check' },
        { key: 'is_active', label: 'Active', type: 'check' },
      ]}
    />
  );
}

export function WabaQuickReplies() {
  return (
    <CrudPage
      {...P} helpPage="settings" icon="⚡️" title="Quick replies"
      endpoint={`${WABA_V1}/quick-replies/`}
      searchPlaceholder="Search…"
      createLabel="+ Quick reply"
      fields={[
        { key: 'title', label: 'Title', required: true },
        { key: 'shortcode', label: 'Shortcut', required: true, hint: 'e.g. /hello' },
        { key: 'content', label: 'Content', type: 'textarea', required: true },
        { key: 'is_global', label: 'Visible to all agents', type: 'check' },
      ]}
    />
  );
}

export function WabaAutoReplies() {
  return (
    <CrudPage
      {...P} helpPage="settings" icon="🤖" title="Keyword auto-replies"
      endpoint={`${WABA_V1}/auto-replies/`}
      createLabel="+ Auto-reply"
      fields={[
        { key: 'name', label: 'Name', required: true },
        { key: 'trigger_type', label: 'Trigger type' },
        { key: 'trigger_keywords', label: 'Keywords JSON', type: 'json' },
        { key: 'response_text', label: 'Response', type: 'textarea' },
        { key: 'is_active', label: 'Active', type: 'check' },
        { key: 'priority', label: 'Priority', type: 'number' },
      ]}
    />
  );
}

export function WabaTeams() {
  return (
    <CrudPage
      {...P} helpPage="settings" icon="👥" title="Teams"
      endpoint={`${WABA_V1}/teams/`}
      createLabel="+ Team"
      fields={[
        { key: 'name', label: 'Name', required: true },
        { key: 'description', label: 'Description', type: 'textarea' },
      ]}
    />
  );
}

export function WabaFormDialogs() {
  return (
    <CrudPage
      {...P} helpPage="hooks" icon="📝" title="Form-over-WhatsApp"
      endpoint={`${WABA_API}/form-dialogs/`}
      createLabel="+ Form dialog"
      extraActions={<UniActionButton hero to="/waba-chat/form-sessions">Sessions</UniActionButton>}
      fields={[
        { key: 'form_name', label: 'Label' },
        { key: 'waba_config', label: 'WhatsApp number id', type: 'number', required: true },
        { key: 'form_id', label: 'Uni Form UUID', required: true },
        { key: 'audience', label: 'Audience', type: 'select', options: [
          { value: 'any', label: 'Anyone' },
          { value: 'customer', label: 'Customers' },
          { value: 'distributor', label: 'Distributors' },
        ] },
        { key: 'trigger_phrases', label: 'Trigger phrases', type: 'textarea' },
        { key: 'is_enabled', label: 'Enabled', type: 'check' },
      ]}
    />
  );
}

export function WabaFormSessions() {
  return (
    <CrudPage
      {...P} helpPage="hooks" icon="📝" title="Form dialog sessions"
      endpoint={`${WABA_API}/form-sessions/`}
      readOnly
      fields={[
        { key: 'status', label: 'Status' },
        { key: 'phone_number', label: 'Phone' },
      ]}
    />
  );
}

export function WabaKycSessions() {
  return (
    <CrudPage
      {...P} helpPage="hooks" icon="🪪" title="KYC sessions"
      endpoint={`${WABA_API}/kyc-sessions/`}
      readOnly
      fields={[
        { key: 'status', label: 'Status' },
        { key: 'phone_number', label: 'Phone' },
      ]}
    />
  );
}

export function WabaNotes() {
  return (
    <CrudPage
      {...P} helpPage="thread" icon="🗒" title="Chat notes"
      endpoint={`${WABA_V1}/notes/`}
      createLabel="+ Note"
      fields={[
        { key: 'conversation', label: 'Conversation id', type: 'number', required: true },
        { key: 'content', label: 'Note', type: 'textarea', required: true },
      ]}
    />
  );
}

export function WabaHealth() {
  const { raw, loading, error } = useUniList(`${WABA_V1}/account/health/`);
  return (
    <UniPageShell
      {...P} icon="💚" title="Account health" helpPage="settings"
      actions={<UniActionButton hero to={HOME}>← Home</UniActionButton>}
    >
      {loading && <div className="uni-state">Loading…</div>}
      {error && <div className="ty-error">{String(error.message || error)}</div>}
      <pre className="uni-note" style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(raw, null, 2)}</pre>
    </UniPageShell>
  );
}

export function WabaHooks() {
  return (
    <HookTester {...P} helpPage="hooks" icon="🔌" title="Module hooks" />
  );
}

export function WabaHelp() {
  return <HelpIndexPage {...P} helpPage="help" icon="❓" title="WhatsApp help centre" />;
}

export function WabaFeatures() {
  return <FeaturesPage {...P} helpPage="features" icon="✨" title="WhatsApp features" />;
}

export { getJSON };
